import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";

// The streams map, with one revision per key: reading a key subscribes to that key alone, so one
// agent's row re-runs its own readers and nobody else's. Iterating subscribes to every change.
export class TrackedStreamMap<V> extends Map<string, V> {
  // SvelteMap, not one $state per key: a $state born during an effect is not a dependency of it.
  readonly #revisions = new SvelteMap<string, number>();
  // A key with no revision yet: its readers wait here until the key is announced.
  #membership = $state(0);
  #anyChange = $state(0);
  // Added or removed, not yet notified: `ensure` may run inside a derived, which cannot write.
  readonly #unannounced = new Set<string>();
  readonly #batched = new Set<string>();
  #batchDepth = 0;
  #batchedAll = false;

  #read(key: string): void {
    if (untrack(() => this.#revisions.has(key))) this.#revisions.get(key);
    else void this.#membership;
  }

  override get(key: string): V | undefined {
    this.#read(key);
    return super.get(key);
  }

  override has(key: string): boolean {
    this.#read(key);
    return super.has(key);
  }

  override set(key: string, value: V): this {
    this.#unannounced.add(key);
    return super.set(key, value);
  }

  override delete(key: string): boolean {
    this.#unannounced.add(key);
    return super.delete(key);
  }

  override clear(): void {
    for (const key of super.keys()) this.#unannounced.add(key);
    super.clear();
  }

  override get size(): number {
    void this.#anyChange;
    return super.size;
  }

  override keys(): MapIterator<string> {
    void this.#anyChange;
    return super.keys();
  }

  override values(): MapIterator<V> {
    void this.#anyChange;
    return super.values();
  }

  override entries(): MapIterator<[string, V]> {
    void this.#anyChange;
    return super.entries();
  }

  override [Symbol.iterator](): MapIterator<[string, V]> {
    return this.entries();
  }

  override forEach(
    callback: (value: V, key: string, map: Map<string, V>) => void,
    thisArg?: unknown,
  ): void {
    void this.#anyChange;
    super.forEach(callback, thisArg);
  }

  // No key: a store-wide change, every reader re-runs.
  notify(key?: string): void {
    if (this.#batchDepth > 0) {
      if (key === undefined) this.#batchedAll = true;
      else this.#batched.add(key);
      return;
    }
    this.#publish(key === undefined ? undefined : [key]);
  }

  // Every notify inside `run` lands once, when the outermost batch ends.
  batch(run: () => void): void {
    this.#batchDepth += 1;
    try {
      run();
    } finally {
      this.#batchDepth -= 1;
      if (this.#batchDepth === 0) this.#publishBatch();
    }
  }

  #publishBatch(): void {
    const all = this.#batchedAll;
    const keys = [...this.#batched];
    this.#batchedAll = false;
    this.#batched.clear();
    if (all || keys.length > 0 || this.#unannounced.size > 0) {
      this.#publish(all ? undefined : keys);
    }
  }

  #publish(keys: readonly string[] | undefined): void {
    const dirty = new Set([...(keys ?? super.keys()), ...this.#unannounced]);
    this.#unannounced.clear();
    let membershipMoved = keys === undefined;
    for (const key of dirty) {
      if (!super.has(key)) {
        membershipMoved = true;
        this.#revisions.delete(key);
        continue;
      }
      const revision = untrack(() => this.#revisions.get(key));
      if (revision === undefined) membershipMoved = true;
      this.#revisions.set(key, (revision ?? 0) + 1);
    }
    if (membershipMoved) this.#membership = untrack(() => this.#membership) + 1;
    this.#anyChange = untrack(() => this.#anyChange) + 1;
  }
}
